import { DataTable } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import { toWorker } from "../util/toWorker.ts";
import { getCols } from "../../common/getCols.ts";
import type { View } from "../../common/types.ts";
import { wrappedMood } from "../components/Mood.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { wrappedCurrency } from "../components/wrappedCurrency.ts";
import { SafeHtml } from "../components/SafeHtml.tsx";
import { NegotiateButtons } from "../components/NegotiateButtons.tsx";
import { RosterComposition } from "../components/RosterComposition.tsx";
import { RosterSalarySummary } from "../components/RosterSalarySummary.tsx";
import { confirm } from "../util/confirm.tsx";
import {
	NegotiationModal,
	useNegotiaionModal,
} from "../components/NegotiationModal.tsx";
import { useLocal } from "../util/local.ts";
import { contractAmountForYears } from "../../common/contractYearAmount.ts";
import clsx from "clsx";
import { useState } from "react";

const NegotiationList = ({
	capSpace,
	draftPickAutoContract,
	minContractLength,
	maxContractLength,
	mp,
	numRosterSpots,
	payroll,
	players,
	stats,
	sumContracts,
	userPlayers,
}: View<"negotiationList">) => {
	const {
		challengeNoRatings,
		minContract,
		maxContract,
		salaryCapType,
		spectator,
		season,
	} = useLocal([
		"challengeNoRatings",
		"minContract",
		"maxContract",
		"salaryCapType",
		"spectator",
		"season",
	]);
	const [mpYears, setMpYears] = useState<Record<number, string>>({});
	const [mpBusy, setMpBusy] = useState<number | undefined>(undefined);

	const title =
		salaryCapType === "hard" || !draftPickAutoContract
			? "Rookies and Expiring Contracts"
			: "Re-sign Players";

	useTitleBar({ title });

	const negotiationModal = useNegotiaionModal();

	if (spectator) {
		return <p>The AI will handle re-signing players in spectator mode.</p>;
	}

	const cols = getCols(
		[
			"Name",
			"Pos",
			"Age",
			"Ovr",
			"Pot",
			...stats.map((stat) => `stat:${stat}`),
			"Acquired",
			"Mood",
			"Old Salary",
			"Asking For",
			"Exp",
			"Actions",
		],
		{
			Actions: {
				width: "1px",
			},
		},
	);

	// Multiplayer re-sign window: the pick goes through the server so every
	// league applies the same contract; asking price comes from the shared
	// candidate row
	const mpCandidates = new Map(
		(mp?.myResignCandidates ?? []).map((c) => [c.pid, c]),
	);
	// The player's preferred length is where his asking price is lowest; default
	// the selector there so the initial number matches the "Asking For" column.
	const preferredYears = (exp: number) =>
		Math.min(Math.max(exp - season, minContractLength), maxContractLength);
	const selectedYears = (pid: number, exp: number) =>
		mpYears[pid] !== undefined
			? Number.parseInt(mpYears[pid]!)
			: preferredYears(exp);
	const mpResign = async (pid: number) => {
		if (!mp) {
			return;
		}
		const candidate = mpCandidates.get(pid);
		setMpBusy(pid);
		try {
			const result = await toWorker("main", "mpResignPlayer", {
				leagueId: mp.leagueId,
				pid,
				years: candidate
					? selectedYears(pid, candidate.exp)
					: Number.parseInt(mpYears[pid] ?? "2"),
			});
			if (result.status !== "resigned") {
				logEvent({
					type: "error",
					text:
						result.status === "not_your_player"
							? "That isn't one of your players."
							: result.status === "refuses"
								? "This player refuses to re-sign with you, no matter what you offer."
								: "This player is no longer available to re-sign.",
					saveToDb: false,
				});
			}
		} catch (error) {
			logEvent({
				type: "error",
				text: error instanceof Error ? error.message : String(error),
				saveToDb: false,
			});
		}
		setMpBusy(undefined);
	};

	const rows: DataTableRow[] = players.map((p) => {
		const mpCandidate = mp ? mpCandidates.get(p.pid) : undefined;
		// What this player asks for at the currently-selected number of years
		const mpAskingAmount = mpCandidate
			? contractAmountForYears({
					baseAmount: mpCandidate.amount,
					desiredExp: mpCandidate.exp,
					ovr: mpCandidate.ovr,
					years: selectedYears(p.pid, mpCandidate.exp),
					season,
					minContractLength,
					maxContractLength,
					minContract,
					maxContract,
				})
			: undefined;
		const negotiateButtons = mp ? (
			mp.resignActive && mpCandidate && !mpCandidate.willing ? (
				// Mood-based refusal, exactly like vanilla's "Refuses!" on this page:
				// no number of years and no amount will get him to re-sign
				<span className="text-danger">Refuses to re-sign!</span>
			) : mp.resignActive && mpCandidate ? (
				<>
					<select
						className="form-select form-select-sm w-auto"
						value={String(selectedYears(p.pid, mpCandidate.exp))}
						onChange={(event) => {
							setMpYears((prev) => ({
								...prev,
								[p.pid]: event.target.value,
							}));
						}}
					>
						{Array.from(
							{ length: maxContractLength - minContractLength + 1 },
							(_, i) => i + minContractLength,
						).map((y) => (
							<option key={y} value={y}>
								{y} {y === 1 ? "yr" : "yrs"}
							</option>
						))}
					</select>
					<button
						type="button"
						className="btn btn-primary btn-xs"
						disabled={mpBusy !== undefined}
						onClick={() => mpResign(p.pid)}
					>
						Re-sign
					</button>
				</>
			) : (
				<span className="text-body-secondary">
					{mp.resignActive ? "Not available" : "Window closed"}
				</span>
			)
		) : (
			<NegotiateButtons
				canGoOverCap={salaryCapType === "none" || salaryCapType === "soft"}
				capSpace={capSpace}
				minContract={minContract}
				onNegotiate={async () => {
					await negotiationModal.negotiate(p.pid);
				}}
				spectator={spectator}
				p={p}
				willingToNegotiate={p.mood.user.willing}
			/>
		);

		return {
			key: p.pid,
			metadata: {
				type: "player",
				pid: p.pid,
				season,
				playoffs: "regularSeason",
			},
			data: [
				wrappedPlayerNameLabels({
					pid: p.pid,
					injury: p.injury,
					jerseyNumber: p.jerseyNumber,
					skills: p.ratings.skills,
					defaultWatch: p.watch,
					firstName: p.firstName,
					firstNameShort: p.firstNameShort,
					lastName: p.lastName,
				}),
				p.ratings.pos,
				p.age,
				!challengeNoRatings ? p.ratings.ovr : null,
				!challengeNoRatings ? p.ratings.pot : null,
				...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
				{
					value: <SafeHtml dirty={p.latestTransaction} />,
					searchValue: p.latestTransaction,
					sortValue: p.latestTransactionSeason,
				},
				wrappedMood({
					defaultType: "user",
					maxWidth: true,
					p,
				}),
				{
					...wrappedCurrency(p.lastSalary, "M"),
					classNames: "text-body-secondary",
				},
				wrappedCurrency(
					(mpAskingAmount ?? p.mood.user.contractAmount) / 1000,
					"M",
				),
				p.contract.exp,
				{
					value: (
						<>
							{negotiateButtons}
							{!mp ? (
								<button
									type="button"
									className={clsx(
										"btn btn-light-bordered btn-xs",
										p.mood.user.willing ? undefined : "ms-auto",
									)}
									onClick={async () => {
										await toWorker("main", "cancelContractNegotiation", p.pid);
									}}
								>
									Release
								</button>
							) : null}
						</>
					),
					classNames: "d-flex align-items-center gap-2",
					searchValue: mp
						? mpCandidate && !mpCandidate.willing
							? "Refuses!"
							: "Re-sign"
						: p.mood.user.willing
							? "Negotiate Sign Release"
							: "Refuses! Release",
				},
			],
			classNames: {
				"table-info": p.contract.rookie,
			},
		};
	});

	const hasRookies = players.some((p) => p.contract.rookie);

	return (
		<>
			<RosterComposition className="float-end mb-3" players={userPlayers} />

			<p>
				More:{" "}
				<a href={helpers.leagueUrl(["upcoming_free_agents"])}>
					Upcoming Free Agents
				</a>
			</p>

			{mp ? (
				<div
					className={`alert d-inline-block ${mp.resignActive ? "alert-primary" : "alert-secondary"}`}
				>
					{mp.resignActive ? (
						<>
							<b>Multiplayer re-sign window is open.</b> Re-sign the expiring
							players you want to keep (Bird rights let you go over the cap).
							Anyone you skip becomes a free agent in the auction. When you're
							done, ready up on the{" "}
							<a href={helpers.leagueUrl(["multiplayer"])}>Multiplayer</a> page.
						</>
					) : (
						<>
							In this multiplayer league, re-signing happens at the{" "}
							<b>Re-sign players</b> checkpoint. This page unlocks when the
							window opens.
						</>
					)}
				</div>
			) : salaryCapType === "soft" ? (
				<p>
					You are allowed to go over the salary cap to re-sign your players
					before they become free agents. If you do not re-sign them before free
					agency begins, they will be free to sign with any team, and you won't
					be able to go over the salary cap to sign them.
				</p>
			) : null}

			<RosterSalarySummary
				capSpace={capSpace}
				numRosterSpots={numRosterSpots}
				payroll={payroll}
			/>

			<p>
				Your unsigned players are asking for a total of{" "}
				<b>{helpers.formatCurrency(sumContracts, "M")}</b>.
				{hasRookies ? (
					<>
						{" "}
						Rookies you just drafted are{" "}
						<span className="text-info">highlighted in blue</span>.
					</>
				) : null}
			</p>

			{!mp &&
			(salaryCapType !== "hard" || sumContracts < capSpace) &&
			players.length > 0 ? (
				<button
					className="btn btn-secondary mb-3"
					onClick={async () => {
						const proceed = await confirm(
							`Are you sure you want to re-sign all ${
								players.length
							} ${helpers.plural("player", players.length)}?`,
							{
								okText: "Re-sign all",
							},
						);
						if (!proceed) {
							return;
						}

						const errorMsg = await toWorker("main", "reSignAll", players);

						if (errorMsg) {
							logEvent({
								type: "error",
								text: errorMsg,
								saveToDb: false,
							});
						}
					}}
				>
					Re-sign all
				</button>
			) : null}

			<DataTable
				cols={cols}
				defaultSort={[10, "desc"]}
				name="NegotiationList"
				rows={rows}
			/>

			<NegotiationModal {...negotiationModal.props} />
		</>
	);
};

export default NegotiationList;
